import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandMark } from '../components/BrandMark';
import { authClient, getSessionSafe } from '../lib/auth-client';
import { redirectIfLandingOnly } from '../lib/landing-mode';

export const Route = createFileRoute('/login')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (data?.session) {
      throw redirect({ to: '/home' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const { error: signInError } = await authClient.signIn.magicLink({
        email,
        callbackURL: `${window.location.origin}/home`,
      });
      if (signInError) {
        setError(signInError.message ?? t('login.errorFallback'));
        setSubmitting(false);
        return;
      }
      await navigate({ to: '/verify', search: { email } });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.errorFallback'));
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center text-center animate-fadeUp">
      <BrandMark />
      <div className="mt-10 max-w-md">
        <h1 className="font-display text-3xl font-light text-ash-100 sm:text-4xl">
          {t('login.heading')}
        </h1>
        <p className="mt-5 font-body text-ash-300 leading-relaxed sm:text-lg">
          {t('login.body')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-12 w-full max-w-sm">
        <label htmlFor="email" className="sr-only">
          {t('login.emailLabel')}
        </label>
        <input
          id="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('login.emailPlaceholder')}
          className="w-full border-b border-ash-700 bg-transparent pb-3 text-center font-body text-ash-100 text-lg placeholder:text-ash-500 focus:border-moonlit-400 focus:outline-none"
        />
        <button
          type="submit"
          disabled={submitting || email.length === 0}
          className="mt-10 w-full border border-ash-700 px-8 py-3 font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:border-moonlit-400 hover:text-ash-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submitting ? t('login.submitting') : t('login.submit')}
        </button>
        {error && (
          <p className="mt-6 font-body text-ember-400 text-sm" role="alert">
            {error}
          </p>
        )}
      </form>

      <div className="hairline mt-16 h-px w-32" />
    </div>
  );
}
